import type { FreeStringVFrame } from '@festina/life-general-types/life'
import {
  dateUtils,
  type Except,
  type SetOptional,
  translationUtils,
} from '@festina-life/core'
import { FormikSelect } from '@festina-life/ui/formikForms'
import { usePercentageFormatter, useTranslate } from '@festina-life/ui/hooks'
import get from 'lodash-es/get'
import type { ComponentProps } from 'react'

import { isFieldRequired } from '../../forms/formUtility'
import { useLifeFormikContext } from '../../forms/useLifeFormikContext'
import {
  isBooleanVFrame,
  isDateVFrame,
  isDontUseVFrame,
  isEnumeratedVFrame,
  isFixedVFrame,
  isFractionVFrame,
} from '../../forms/yup/validation/types'
import { isVFrameWithDefaultValue } from '../../forms/yup/validation/yupValidationUtils'
import { useFieldVFrame } from '../useFieldVFrame'

// @BETA consider using InputPropsFromFormik util as the others but not sure if we just delete this component all together
type Props<T> = SetOptional<
  Except<ComponentProps<typeof FormikSelect<T>>, 'isRequired'>,
  'options'
>

export const LifeSelect = <T,>({ name, options, ...restProps }: Props<T>) => {
  const translate = useTranslate()
  const { formatPercentWithDecimals } = usePercentageFormatter()

  const { values, setFieldValue } = useLifeFormikContext()
  const frameStatus = useFieldVFrame(name)

  if (isDontUseVFrame(frameStatus) && !isEnumeratedVFrame(frameStatus)) {
    return null
  }

  const value = get(values, name as string)
  const fixed = isFixedVFrame(frameStatus)
  const isRequired = isFieldRequired(frameStatus)

  let frameOptions: T[] = []
  let renderFn = restProps.renderFn

  if (isEnumeratedVFrame(frameStatus)) {
    frameOptions = [...(frameStatus.values ?? [])] as T[]

    if (isBooleanVFrame(frameStatus)) {
      renderFn = (x: T) => translationUtils.translateBoolean(!!x, translate)
    } else if (isFractionVFrame(frameStatus)) {
      renderFn = (x) => formatPercentWithDecimals(x as number)
    } else if (isDateVFrame(frameStatus)) {
      renderFn = (x) => dateUtils.dateAsStr(x as string)
    }
  } else if (isBooleanVFrame(frameStatus)) {
    frameOptions = [true, false] as T[]
    renderFn = (x) => translationUtils.translateBoolean(!!x, translate)
  }

  const defaultValue = isVFrameWithDefaultValue(frameStatus)
    ? ((frameStatus as FreeStringVFrame)?.defaultValue as T)
    : undefined

  return (
    <FormikSelect
      name={name}
      disabled={fixed}
      defaultValue={defaultValue}
      onDefaultValueClick={() => setFieldValue(name, defaultValue)}
      value={fixed ? frameStatus.value : value}
      isRequired={isRequired}
      options={fixed ? [frameStatus.value as T] : (options ?? frameOptions)}
      renderFn={renderFn}
      hasRightColumnAlways
      {...restProps}
    />
  )
}
